/**
 * React Hook for Stripe Connect Status Management
 * Provides easy access to Connect status and UI state management with polling
 */

import { useState, useEffect, useContext, useCallback, useRef } from "react";
import { AuthContext } from "../context/AuthProvider";
import {
  checkLandlordConnectStatus,
  getConnectStatusUI,
  initializeConnectAccount,
} from "../lib/payments/connectStatus";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../helpers/firebase";

/**
 * Hook for managing Stripe Connect status with real-time updates
 * @param {string} landlordId - Optional landlord ID (defaults to current user)
 * @param {Object} options - Configuration options
 * @param {boolean} options.enablePolling - Enable automatic status polling (default: true)
 * @param {number} options.pollInterval - Polling interval in milliseconds (default: 30000)
 * @returns {Object} Connect status and UI state
 */
export const useConnectStatus = (landlordId = null, options = {}) => {
  const { user } = useContext(AuthContext);
  const [connectStatus, setConnectStatus] = useState(null);
  const [uiState, setUiState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastChecked, setLastChecked] = useState(null);
  const inFlightRef = useRef(false);

  const {
    // Default to NO polling to avoid repeated backend + Firestore reads from multiple
    // components using this hook at once.
    enablePolling = false,
    pollInterval = 1000 * 60 * 5, // 5 minutes
  } = options;

  const targetLandlordId = landlordId || user?.uid;

  const checkStatus = useCallback(async () => {
    if (!targetLandlordId) {
      setLoading(false);
      return;
    }

    // Prevent stacked calls (multiple components / intervals / focus handlers)
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    try {
      setError(null);

      // Try backend API first
      try {
        const status = await checkLandlordConnectStatus(targetLandlordId);

        // If backend reports an error status, fall back to Firebase
        if (status && status.status === "error") {
          throw new Error(status.error || "Backend returned error status");
        }

        setConnectStatus(status);
        setUiState(getConnectStatusUI(status));
        setLastChecked(new Date().toISOString());
        return;
      } catch (apiError) {
        if (process.env.NODE_ENV === "development") {
          console.log(
            "Backend API not available, checking Firebase directly:",
            apiError.message
          );
        }

        // Fallback to Firebase direct check
        const userRef = doc(db, "users", targetLandlordId);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const userData = userSnap.data();
          const connectStatus = userData.connectStatus || {};

          // Determine status from Firebase data
          const hasConnectId = userData.stripeConnectId || connectStatus.id;
          const chargesEnabled = connectStatus.charges_enabled || false;
          const payoutsEnabled = connectStatus.payouts_enabled || false;
          const detailsSubmitted = connectStatus.details_submitted || false;

          let status = "incomplete";
          if (hasConnectId && !chargesEnabled && !detailsSubmitted) {
            status = "pending"; // KYC pending
          } else if (chargesEnabled && payoutsEnabled) {
            status = "active";
          } else if (hasConnectId && detailsSubmitted && !chargesEnabled) {
            status = "pending_verification";
          }

          const fallbackStatus = {
            canAcceptPayments: chargesEnabled,
            canReceivePayouts: payoutsEnabled,
            isFullyOnboarded: chargesEnabled && payoutsEnabled,
            pendingRequirements: [],
            futureRequirements: [],
            pastDueRequirements: [],
            pendingVerification: [],
            deauthorized: false,
            lastUpdated: connectStatus.updatedAt || userData.updatedAt,
            status: status,
            rawStatus: connectStatus,
          };

          setConnectStatus(fallbackStatus);
          setUiState(getConnectStatusUI(fallbackStatus));
          setLastChecked(new Date().toISOString());
        } else {
          // No user document found
          const emptyStatus = {
            canAcceptPayments: false,
            canReceivePayouts: false,
            isFullyOnboarded: false,
            pendingRequirements: [],
            futureRequirements: [],
            pastDueRequirements: [],
            pendingVerification: [],
            deauthorized: false,
            lastUpdated: null,
            status: "incomplete",
            rawStatus: {},
          };

          setConnectStatus(emptyStatus);
          setUiState(getConnectStatusUI(emptyStatus));
          setLastChecked(new Date().toISOString());
        }
      }
    } catch (err) {
      console.error("Error checking Connect status:", err);
      setError(err.message);
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, [targetLandlordId]);

  // Initial status check
  useEffect(() => {
    if (targetLandlordId) {
      checkStatus();
    } else {
      setLoading(false);
    }
  }, [targetLandlordId, checkStatus]);

  // Polling for real-time updates
  useEffect(() => {
    if (!enablePolling || !targetLandlordId) return;

    const interval = setInterval(() => {
      // Avoid work while tab is backgrounded (reduces repeated reads)
      if (
        typeof document !== "undefined" &&
        document.visibilityState !== "visible"
      )
        return;
      checkStatus();
    }, pollInterval);

    return () => clearInterval(interval);
  }, [enablePolling, targetLandlordId, pollInterval, checkStatus]);

  const refreshStatus = () => {
    checkStatus();
  };

  return {
    connectStatus,
    uiState,
    loading,
    error,
    lastChecked,
    refreshStatus,
    checkStatus,
    // Convenience properties
    canAcceptPayments: connectStatus?.canAcceptPayments || false,
    isFullyOnboarded: connectStatus?.isFullyOnboarded || false,
    showConnectButton: uiState?.showConnectButton || false,
    showRentPaymentForm: uiState?.showRentPaymentForm || false,
    alertMessage: uiState?.alertMessage || "",
    alertType: uiState?.alertType || "info",
  };
};

/**
 * Hook for Connect button component
 * Handles the Connect account initialization flow
 * @param {string} landlordId - Optional landlord ID (defaults to current user)
 * @returns {Object} Connect button state and handlers
 */
export const useConnectButton = (landlordId = null) => {
  const { user } = useContext(AuthContext);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);

  const targetLandlordId = landlordId || user?.uid;

  const handleConnect = async () => {
    try {
      setIsConnecting(true);
      setError(null);

      // Use the imported initialization function
      const result = await initializeConnectAccount(targetLandlordId);

      if (result.success) {
        // Redirect to Stripe onboarding
        window.location.href = result.onboardingUrl;
      } else {
        setError(result.error);
      }
    } catch (err) {
      console.error("Error connecting account:", err);
      setError(err.message);
    } finally {
      setIsConnecting(false);
    }
  };

  return {
    isConnecting,
    error,
    handleConnect,
    disabled: !targetLandlordId || isConnecting,
  };
};
